import {
  Activity,
  Clock,
  TrendingDown,
  TrendingUp,
  Globe,
  Package,
  Timer,
  Repeat,
  Network,
  ShieldCheck,
  CheckCheck,
  X,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  DiagnosticsService,
  findParam,
  DSCP_POST_VALUE,
  type DiagnosticParam,
  type DiagnosticParamPost,
} from '@/services/diagnosticsService';
import {
  DiagnosticAbortError,
  DiagnosticTimeoutError,
  DiagnosticTriggerError,
  isCompleteStatus,
  type PingSseResult,
} from '@/services/diagnosticsSse';
import { useDiagnosticRunner } from '@/hooks/useDiagnosticRunner';
import { getLatencyThresholdColor } from '@/utils/statusColor';
import {
  MainContainer,
  CardsContainer,
  ToolHeaderRow,
  ToolTitleGroup,
  ToolTitleText,
  ToolContentBox,
  StatusRow,
  HostTitleGroup,
  HostNameText,
  MetricHeaderGroup,
  MetricValueGroup,
  StatItemStart,
  StatItemCenter,
  StatItemEnd,
  StatItemNumberText,
  ConfigTitleGroup,
  ConfigItemHeader,
  TwoCardsRow,
  CardColumn,
  FullHeightCard,
  CardContentWrapper,
  ConfigCardContentWrapper,
  LoadingOverlay,
  LoadingText,
  LossProgressBar,
  ProgressBarContainer,
  PackagesRow,
  ConfigGrid,
  ConfigItem,
  StatusBadge,
  StatusBadgeText,
  TimeMetricsGrid,
  TimeMetricCard,
  EmptyState,
  EmptyStateIconBox,
  EmptyStateText,
  HeaderIconBox,
  ConfigHeaderLabel,
  ConfigValueText,
  MetricLabelText,
  MetricValueText,
  MetricUnitText,
  StatLabelText,
  SectionDivider,
} from './styles';
import { useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export interface PingRef {
  startTest: (ipToTest: string, customTimeout?: string | number) => void;
  resetTest: () => void;
}

interface PingProps {
  onStateChange?: (state: { isRunning: boolean; isDone: boolean }) => void;
  equipmentId: string;
}

// TR-069 IPPing input parameters (writable at the CWMP level).
const PING_INPUT_SUFFIXES = [
  'Host',
  'DSCP',
  'NumberOfRepetitions',
  'Timeout',
  'DataBlockSize',
  'PackagesSize',
  'PacketSize',
];

function isPingInput(param: DiagnosticParam): boolean {
  return PING_INPUT_SUFFIXES.some((suffix) => param.name?.endsWith(suffix));
}

interface PingStats {
  state: string;
  host: string;
  successCount: string;
  failCount: string;
  avgTime: string;
  minTime: string;
  maxTime: string;
  timeout: string;
  repetitions: string;
  packetSize: string;
  dscp: string;
  interfaceTest: string;
}

const INITIAL_STATS: PingStats = {
  state: 'None',
  host: '',
  successCount: 'N/A',
  failCount: 'N/A',
  avgTime: 'N/A',
  minTime: 'N/A',
  maxTime: 'N/A',
  timeout: 'N/A',
  repetitions: 'N/A',
  packetSize: 'N/A',
  dscp: 'N/A',
  interfaceTest: 'N/A',
};

const Ping = forwardRef<PingRef, PingProps>(
  ({ onStateChange, equipmentId }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const [testActive, setTestActive] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [stats, setStats] = useState<PingStats>(INITIAL_STATS);
    const { run, cancel } = useDiagnosticRunner();

    const startTest = useCallback(
      async (ipToTest: string, customTimeout?: string | number) => {
        if (!equipmentId) {
          enqueueSnackbar(t('tools.errors.invalidEquipmentOrInterface'), {
            variant: 'error',
          });
          return;
        }
        setTestActive(true);
        setIsDone(false);
        setStats({
          ...INITIAL_STATS,
          state: 'Requested',
          host: ipToTest,
          timeout: customTimeout ? String(customTimeout) : 'N/A',
        });

        try {
          // GET params for the ping diagnostic (to get the param IDs) POST body
          const { params, stateParamId, dscpParamId } =
            await DiagnosticsService.getParams(equipmentId, 'ping');

          // Mount body for the POST request, using the param IDs and the input values
          let body: DiagnosticParamPost[];
          if (params.length > 0) {
            const hostParam = findParam(params, 'host', 'Host');
            const timeoutParam = findParam(params, 'timeout', 'Timeout');
            const inputParams = params.filter(isPingInput).map(
              (p): DiagnosticParamPost => ({
                id: p.id,
                value:
                  p.id === dscpParamId
                    ? DSCP_POST_VALUE
                    : p.id === hostParam?.id
                      ? ipToTest
                      : p.id === timeoutParam?.id && customTimeout
                        ? String(customTimeout)
                        : (p.value ?? ''),
              })
            );
            const stateParam: DiagnosticParamPost[] = stateParamId
              ? [{ id: stateParamId, value: 'Requested' }]
              : [];
            body = [...stateParam, ...inputParams];
          } else {
            body = [{ id: 0, value: ipToTest }];
          }

          // Packet size is not part of the SSE result payload — fall back to
          // the value we requested.
          const requestedPacketSize =
            findParam(params, 'PackagesSize', 'PacketSize')?.value ?? 'N/A';

          // Opens the SSE stream, POSTs the trigger (202) and resolves with
          // the DiagnosticResult event sent before the stream closes.
          const { result } = await run<PingSseResult>(
            equipmentId,
            'ping',
            body
          );

          setStats({
            state: isCompleteStatus(result.status)
              ? 'Complete'
              : result.successCount
                ? 'Complete'
                : 'N/A',
            host: result.host ?? ipToTest,
            successCount: result.successCount ?? 'N/A',
            failCount: result.failureCount ?? 'N/A',
            avgTime: result.averageTime ?? 'N/A',
            minTime: result.minimumTime ?? 'N/A',
            maxTime: result.maximumTime ?? 'N/A',
            timeout:
              result.timeout ?? (customTimeout ? String(customTimeout) : 'N/A'),
            repetitions: result.numberRepetitions ?? 'N/A',
            packetSize: requestedPacketSize,
            dscp: result.dscp ?? 'N/A',
            interfaceTest: result.interfaceTest ?? 'N/A',
          });
          setTestActive(false);
          setIsDone(true);
        } catch (error) {
          // A cancelled run already had its UI state restored by resetTest.
          if (error instanceof DiagnosticAbortError) return;

          enqueueSnackbar(
            error instanceof DiagnosticTimeoutError
              ? t('tools.errors.diagnosticTimeout')
              : error instanceof DiagnosticTriggerError
                ? t('tools.errors.diagnosticTriggerFailed')
                : t('tools.errors.pingFailed'),
            { variant: 'error' }
          );
          setStats((prev) => ({ ...prev, state: 'Error' }));
          setTestActive(false);
          setIsDone(true);
        }
      },
      [equipmentId, run, t]
    );

    const resetTest = useCallback(() => {
      cancel();
      setTestActive(false);
      setIsDone(false);
      setStats(INITIAL_STATS);
    }, [cancel]);

    useImperativeHandle(ref, () => ({
      startTest,
      resetTest,
    }));

    useEffect(() => {
      if (onStateChange) {
        onStateChange({ isRunning: testActive, isDone });
      }
    }, [testActive, isDone, onStateChange]);

    const totalPackages =
      stats.successCount !== 'N/A' && stats.failCount !== 'N/A'
        ? Number(stats.successCount) + Number(stats.failCount)
        : 0;

    const lossPercentage =
      totalPackages > 0
        ? ((Number(stats.failCount) / totalPackages) * 100).toFixed(1)
        : '0.0';

    const successRate =
      totalPackages > 0
        ? ((Number(stats.successCount) / totalPackages) * 100).toFixed(1)
        : null;

    const isComplete =
      isDone && stats.state !== 'Error' && stats.state !== 'None';
    const isError = isDone && stats.state === 'Error';

    const statusInfo = useMemo(() => {
      if (isError) {
        return {
          color: theme.palette.status.offline,
          Icon: X,
          label: t('tools.error'),
        };
      }
      if (stats.state === 'Requested') {
        return {
          color: theme.palette.warning?.main ?? theme.palette.status.warning,
          Icon: Loader2,
          label: t('equipment.latency.running'),
        };
      }
      if (isComplete && parseFloat(lossPercentage) > 0) {
        return {
          color: theme.palette.warning?.main ?? theme.palette.status.warning,
          Icon: AlertTriangle,
          label: stats.state,
        };
      }
      if (isComplete) {
        return {
          color: theme.palette.status.online,
          Icon: CheckCheck,
          label: stats.state,
        };
      }
      return {
        color: theme.palette.grey['01'],
        Icon: Activity,
        label: stats.state || '—',
      };
    }, [isComplete, isError, lossPercentage, stats.state, theme, t]);

    const timeMetrics = useMemo(() => {
      return [
        {
          id: 'min',
          icon: TrendingUp,
          label: t('tools.ping.minResponseTime'),
          value: stats.minTime,
          color: getLatencyThresholdColor(stats.minTime, theme),
        },
        {
          id: 'avg',
          icon: Clock,
          label: t('tools.ping.avgTime'),
          value: stats.avgTime,
          color: getLatencyThresholdColor(stats.avgTime, theme),
        },
        {
          id: 'max',
          icon: TrendingDown,
          label: t('tools.ping.maxResponseTime'),
          value: stats.maxTime,
          color: getLatencyThresholdColor(stats.maxTime, theme),
        },
      ];
    }, [stats, theme, t]);

    const configItems = useMemo(
      () => [
        {
          id: 'host',
          icon: Globe,
          label: t('tools.ping.host'),
          value: stats.host || '—',
        },
        {
          id: 'repetitions',
          icon: Repeat,
          label: t('tools.ping.repetitions'),
          value: stats.repetitions !== 'N/A' ? stats.repetitions : '—',
        },
        {
          id: 'timeout',
          icon: Timer,
          label: t('tools.ping.timeout'),
          value: stats.timeout !== 'N/A' ? `${stats.timeout} ms` : '—',
        },
        {
          id: 'packetSize',
          icon: Package,
          label: t('tools.ping.packetSize'),
          value: stats.packetSize !== 'N/A' ? `${stats.packetSize} B` : '—',
        },
        {
          id: 'dscp',
          icon: ShieldCheck,
          label: t('tools.ping.dscp'),
          value: stats.dscp !== 'N/A' ? stats.dscp : '—',
        },
        {
          id: 'interface',
          icon: Network,
          label: t('tools.ping.interface'),
          value: stats.interfaceTest !== 'N/A' ? stats.interfaceTest : '—',
        },
      ],
      [stats, t]
    );

    return (
      <MainContainer>
        <ToolHeaderRow>
          <ToolTitleGroup>
            <Activity color={theme.palette.white['06']} size={22} />
            <ToolTitleText variant="h3">{t('tools.ping.title')}</ToolTitleText>
          </ToolTitleGroup>
        </ToolHeaderRow>

        <ToolContentBox>
          {testActive && (
            <LoadingOverlay>
              <CircularProgress
                enableTrackSlot
                size="20px"
                sx={{ color: theme.palette.primary.main }}
              />
              <LoadingText variant="body2">
                {t('equipment.latency.running')}
              </LoadingText>
            </LoadingOverlay>
          )}

          <CardsContainer>
            {!isDone && !testActive && (
              <EmptyState>
                <EmptyStateIconBox>
                  <Activity size={36} color={theme.palette.primary.main} />
                </EmptyStateIconBox>
                <EmptyStateText variant="body1">
                  {t('tools.ping.emptyState')}
                </EmptyStateText>
              </EmptyState>
            )}

            {(isDone || testActive) && (
              <TwoCardsRow>
                <CardColumn>
                  <FullHeightCard>
                    <CardContentWrapper>
                      <StatusRow>
                        <HostTitleGroup>
                          <HeaderIconBox>
                            <Globe size={18} />
                          </HeaderIconBox>
                          <HostNameText variant="h4">
                            {stats.host || '—'}
                          </HostNameText>
                        </HostTitleGroup>
                        <StatusBadge $color={statusInfo.color}>
                          <statusInfo.Icon
                            size={14}
                            strokeWidth={2.5}
                            color={theme.palette.white['01']}
                          />
                          <StatusBadgeText variant="caption">
                            {statusInfo.label}
                          </StatusBadgeText>
                        </StatusBadge>
                      </StatusRow>

                      <TimeMetricsGrid>
                        {timeMetrics.map((m) => (
                          <TimeMetricCard key={m.id}>
                            <MetricHeaderGroup>
                              <m.icon size={14} color={m.color} />
                              <MetricLabelText>{m.label}</MetricLabelText>
                            </MetricHeaderGroup>
                            <MetricValueGroup>
                              <MetricValueText
                                variant="h2"
                                $color={
                                  m.value !== 'N/A'
                                    ? m.color
                                    : theme.palette.text.disabled
                                }
                              >
                                {m.value !== 'N/A' ? m.value : '—'}
                              </MetricValueText>
                              {m.value !== 'N/A' && (
                                <MetricUnitText variant="caption">
                                  ms
                                </MetricUnitText>
                              )}
                            </MetricValueGroup>
                          </TimeMetricCard>
                        ))}
                      </TimeMetricsGrid>

                      <SectionDivider />

                      <PackagesRow>
                        <StatItemStart>
                          <StatLabelText variant="caption">
                            {t('equipment.latency.lostPackets')}
                          </StatLabelText>
                          <MetricValueGroup>
                            <Typography
                              color={
                                parseFloat(lossPercentage) > 0
                                  ? theme.palette.status.offline
                                  : theme.palette.status.online
                              }
                              variant="h3"
                              fontWeight={700}
                            >
                              {stats.failCount !== 'N/A'
                                ? stats.failCount
                                : '—'}
                            </Typography>
                            {totalPackages > 0 && (
                              <StatLabelText variant="caption">
                                / {totalPackages} ({lossPercentage}%)
                              </StatLabelText>
                            )}
                          </MetricValueGroup>
                        </StatItemStart>

                        <StatItemCenter>
                          <StatLabelText variant="caption">
                            {t('equipment.latency.sentPackages')}
                          </StatLabelText>
                          <StatItemNumberText variant="h3">
                            {totalPackages > 0 ? totalPackages : '—'}
                          </StatItemNumberText>
                        </StatItemCenter>

                        <StatItemEnd>
                          <StatLabelText variant="caption">
                            {t('tools.ping.successCount')}
                          </StatLabelText>
                          <MetricValueGroup>
                            <Typography
                              color={theme.palette.status.online}
                              variant="h3"
                              fontWeight={700}
                            >
                              {stats.successCount !== 'N/A'
                                ? stats.successCount
                                : '—'}
                            </Typography>
                            {successRate !== null && (
                              <StatLabelText variant="caption">
                                ({successRate}%)
                              </StatLabelText>
                            )}
                          </MetricValueGroup>
                        </StatItemEnd>
                      </PackagesRow>

                      <ProgressBarContainer>
                        <LossProgressBar
                          variant="determinate"
                          value={
                            successRate !== null ? parseFloat(successRate) : 0
                          }
                          $successPercentage={
                            successRate !== null ? parseFloat(successRate) : 0
                          }
                        />
                      </ProgressBarContainer>
                    </CardContentWrapper>
                  </FullHeightCard>
                </CardColumn>

                {isComplete && (
                  <CardColumn>
                    <FullHeightCard>
                      <ConfigCardContentWrapper>
                        <ConfigTitleGroup>
                          <HeaderIconBox>
                            <ShieldCheck size={18} />
                          </HeaderIconBox>
                          <ConfigHeaderLabel variant="caption">
                            {t('tools.ping.configSummary')}
                          </ConfigHeaderLabel>
                        </ConfigTitleGroup>
                        <ConfigGrid>
                          {configItems.map((item) => (
                            <ConfigItem key={item.id}>
                              <ConfigItemHeader>
                                <item.icon
                                  size={14}
                                  color={theme.palette.primary.main}
                                />
                                <MetricLabelText>{item.label}</MetricLabelText>
                              </ConfigItemHeader>
                              <ConfigValueText
                                variant="body1"
                                title={item.value}
                              >
                                {item.value}
                              </ConfigValueText>
                            </ConfigItem>
                          ))}
                        </ConfigGrid>
                      </ConfigCardContentWrapper>
                    </FullHeightCard>
                  </CardColumn>
                )}
              </TwoCardsRow>
            )}
          </CardsContainer>
        </ToolContentBox>
      </MainContainer>
    );
  }
);

export default Ping;
